import { MAKS_DLUGOSC_SZUKANIA_WYWOLAN, MIN_DLUGOSC_PREFIKSU_ID, STATUSY_WYWOLAN, ZAKRESY_WYWOLAN } from "@shared/wywolaniaTypy.js";
import { useEffect, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useZasob } from "../../hooks/useZasob.js";
import { api } from "../../lib/api.js";
import {
  czyZnacznikCzasu,
  etykietaStatusu,
  komunikatBleduApi,
  liczbaWpisow,
  odDlaOkresu,
  OKRESY_FILTRA,
  opisOknaCzasowego,
} from "../../lib/developer.js";
import { etykietaDostawcy, etykietaEtapu } from "../../lib/koszty.js";
import { Button } from "../ui/Button.js";
import { Card } from "../ui/Card.js";
import { Field } from "../ui/Field.js";
import { Select } from "../ui/Select.js";
import { StanBledu, StanLadowania, StanPusty } from "../ui/stany.js";
import { BladOdswiezania } from "./BladOdswiezania.js";
import { PoleSzukania } from "./PoleSzukania.js";
import { Paginacja } from "./Paginacja.js";
import { TabelaWywolan } from "./TabelaWywolan.js";

/**
 * Zakladka "Wywolania": historia wszystkich wywolan modeli z filtrami, szukaniem i paginacja.
 *
 * Stan widoku (filtry i strona) mieszka w ADRESIE (useSearchParams), zeby odswiezenie strony albo powrot ze szczegolu nie
 * gubil miejsca. Zmiana filtra wraca na pierwsza strone. Lista odswieza sie po cichu co 15 s (S-7: bez migotania, stare
 * wiersze zostaja do czasu nowych) i opisuje sama siebie zakresem z odpowiedzi serwera, wiec stare dane nigdy nie wisza
 * pod etykieta innego filtra.
 *
 * Okno czasowe ma dwie postacie: wzgledny `okres` z listy rozwijanej ("ostatnie 24 godziny", liczony od chwili zapytania)
 * i dokladne `od`/`do` z adresu. Druga niesie link z kafelka "Bledy" w Stanie systemu: kafelek liczy bledy od konkretnego
 * momentu, wiec lista pod nim musi liczyc od tego samego momentu (a nie od "teraz minus doba", ktore sie przesuwa). Gdy `od`
 * albo `do` jest w adresie, wygrywa i ma widoczny opis z przyciskiem czyszczenia.
 */

const NA_STRONE = 25;
const ODSWIEZANIE_MS = 15_000;
const DOSTAWCY = ["openai", "gemini", "elevenlabs", "qwen", "ser"] as const;

const WSZYSTKIE = "";
/** Wartosc "okresu" w liscie rozwijanej, gdy okno przyszlo z adresu (`od`/`do`): to nie jest zaden z gotowych okresow. */
const OKNO_Z_ADRESU = "__okno";
const OPCJE_STATUSU = [
  { wartosc: WSZYSTKIE, etykieta: "Wszystkie statusy" },
  ...STATUSY_WYWOLAN.map((s) => ({ wartosc: s as string, etykieta: etykietaStatusu(s).etykieta })),
];
const OPCJE_ZAKRESU = [
  { wartosc: WSZYSTKIE, etykieta: "Wszystkie etapy" },
  ...ZAKRESY_WYWOLAN.map((z) => ({ wartosc: z as string, etykieta: etykietaEtapu(z) })),
];
const OPCJE_DOSTAWCY = [
  { wartosc: WSZYSTKIE, etykieta: "Wszyscy dostawcy" },
  ...DOSTAWCY.map((d) => ({ wartosc: d as string, etykieta: etykietaDostawcy(d) })),
];
const OPCJE_OKRESU = OKRESY_FILTRA.map((o) => ({ wartosc: o.wartosc, etykieta: o.etykieta }));
const OPCJE_OKRESU_Z_OKNEM = [{ wartosc: OKNO_Z_ADRESU, etykieta: "Wybrany przedział" }, ...OPCJE_OKRESU];

interface Filtry {
  q: string;
  status: string;
  zakres: string;
  dostawca: string;
  okres: string;
  /** Dokladne okno z adresu (ISO albo sama data). Pusty napis, gdy brak albo gdy wartosc w adresie nie jest data. */
  od: string;
  do: string;
  sesja: string;
  operacja: string;
  offset: number;
}

/** Wartosc z adresu jako data dla serwera: smiec w `?od=` nie moze zrobic z calej listy bledu 400. */
function dataZAdresu(p: URLSearchParams, klucz: string): string {
  const wartosc = p.get(klucz) ?? "";
  return czyZnacznikCzasu(wartosc) ? wartosc : "";
}

function odczytajFiltry(p: URLSearchParams): Filtry {
  const offset = Number.parseInt(p.get("offset") ?? "0", 10);
  return {
    q: p.get("q") ?? "",
    status: p.get("status") ?? "",
    zakres: p.get("zakres") ?? "",
    dostawca: p.get("dostawca") ?? "",
    okres: p.get("okres") ?? "",
    od: dataZAdresu(p, "od"),
    do: dataZAdresu(p, "do"),
    sesja: p.get("sesja") ?? "",
    operacja: p.get("operacja") ?? "",
    offset: Number.isFinite(offset) && offset > 0 ? offset : 0,
  };
}

export function ListaWywolan(): React.JSX.Element {
  const [params, setParams] = useSearchParams();
  const { search } = useLocation();
  const f = odczytajFiltry(params);
  const poczatekRef = useRef<HTMLDivElement>(null);
  const poprzedniOffset = useRef(f.offset);

  const oknoZAdresu = f.od !== "" || f.do !== "";
  const klucz = JSON.stringify(f);
  const { dane, kluczDanych, ladowanie, blad, odswiez } = useZasob(
    klucz,
    () =>
      api.developerWywolania({
        q: f.q,
        status: f.status,
        zakres: f.zakres,
        dostawca: f.dostawca,
        // Dokladne okno z adresu wygrywa z wzglednym okresem: tak je pokazuje tez lista rozwijana.
        od: oknoZAdresu ? f.od : odDlaOkresu(f.okres),
        do: oknoZAdresu ? f.do : undefined,
        sesja: f.sesja,
        operacja: f.operacja,
        limit: NA_STRONE,
        offset: f.offset,
      }),
    { co: ODSWIEZANIE_MS },
  );

  /** Zmiana filtra: ustawia (albo kasuje) klucze w adresie i wraca na pierwsza strone. `replace`, by nie zasmiecac historii. */
  function zmien(zmiany: Record<string, string>): void {
    const nowe = new URLSearchParams(params);
    for (const [k, v] of Object.entries(zmiany)) {
      if (v === "") nowe.delete(k);
      else nowe.set(k, v);
    }
    if (!("offset" in zmiany)) nowe.delete("offset");
    setParams(nowe, { replace: true });
  }

  // PG-2: zmiana strony przewija widok do gory tabeli.
  useEffect(() => {
    if (poprzedniOffset.current !== f.offset) poczatekRef.current?.scrollIntoView?.({ block: "start" });
    poprzedniOffset.current = f.offset;
  }, [f.offset]);

  // Wiersze z odpowiedzi na INNY filtr albo strone (poprzednie zapytanie) nie sa danymi dla tego, co wybral uzytkownik: gdy
  // pobranie nowych padnie, pokazujemy blad z ponowieniem zamiast starych wierszy pod nowym filtrem. Przy cichym odswiezeniu
  // tego samego widoku (klucz ten sam) stare wiersze zostaja, a nad nimi stoi komunikat.
  const bladBezZgodnychDanych = dane === null || kluczDanych !== klucz;

  const filtrAktywny =
    f.q !== "" || f.status !== "" || f.zakres !== "" || f.dostawca !== "" || f.okres !== "" || oknoZAdresu || f.sesja !== "" || f.operacja !== "";

  // Serwer przycina offset poza zakresem do ostatniej strony i oddaje ten, ktory zastosowal (shared: ListaWywolanDto.offset).
  // Roznica wzgledem adresu to nie "brak wywolan": mowimy, ze strona byla poza lista, a pager opisuje to, co widac.
  const stronaPrzycieta = dane !== null && kluczDanych === klucz && dane.razem > 0 && dane.offset !== f.offset;

  return (
    <Card tytul="Historia wywołań">
      <div className="developer-filtry" ref={poczatekRef}>
        <PoleSzukania
          id="dev-szukaj"
          etykieta="Szukaj"
          wartosc={f.q}
          onZmiana={(q) => zmien({ q })}
          maksDlugosc={MAKS_DLUGOSC_SZUKANIA_WYWOLAN}
          podpowiedz={`Id (pełne albo początek od ${MIN_DLUGOSC_PREFIKSU_ID} znaków), sesja, operacja, model, komunikat błędu. Bez treści zapytań.`}
        />
        <Field etykieta="Status" id="dev-status">
          {(a) => <Select {...a} wartosc={f.status} opcje={OPCJE_STATUSU} onZmiana={(status) => zmien({ status })} />}
        </Field>
        <Field etykieta="Etap" id="dev-zakres">
          {(a) => <Select {...a} wartosc={f.zakres} opcje={OPCJE_ZAKRESU} onZmiana={(zakres) => zmien({ zakres })} />}
        </Field>
        <Field etykieta="Dostawca" id="dev-dostawca">
          {(a) => <Select {...a} wartosc={f.dostawca} opcje={OPCJE_DOSTAWCY} onZmiana={(dostawca) => zmien({ dostawca })} />}
        </Field>
        <Field etykieta="Okres" id="dev-okres">
          {(a) => (
            <Select
              {...a}
              wartosc={oknoZAdresu ? OKNO_Z_ADRESU : f.okres}
              opcje={oknoZAdresu ? OPCJE_OKRESU_Z_OKNEM : OPCJE_OKRESU}
              // Wybor gotowego okresu zastepuje okno z adresu; ponowny wybor "Wybranego przedzialu" nic nie zmienia.
              onZmiana={(okres) => (okres === OKNO_Z_ADRESU ? undefined : zmien({ okres, od: "", do: "" }))}
            />
          )}
        </Field>
      </div>

      {(f.sesja !== "" || f.operacja !== "") && (
        <p className="developer-zawezenie">
          Zawężono do {f.sesja !== "" ? `sesji ${f.sesja}` : `operacji ${f.operacja}`}.{" "}
          <Button onClick={() => zmien({ sesja: "", operacja: "" })}>Pokaż wszystkie</Button>
        </p>
      )}

      {oknoZAdresu && (
        <p className="developer-zawezenie">
          Okno czasowe: {opisOknaCzasowego(f.od, f.do)} (czas lokalny).{" "}
          <Button onClick={() => zmien({ od: "", do: "" })}>Wyczyść okno czasowe</Button>
        </p>
      )}

      {/* Blad nad danymi, nie pod 25 wierszami: widac go od razu, takze gdy poprzedni wynik byl pusty. */}
      {blad !== null && dane !== null && !bladBezZgodnychDanych && (
        <BladOdswiezania czego="listy" blad={blad} ostatnie="Widać ostatnio pobrane wiersze." onPonow={odswiez} />
      )}

      {blad !== null && !ladowanie && bladBezZgodnychDanych ? (
        <StanBledu
          tytul="Nie udało się wczytać historii wywołań"
          opis={komunikatBleduApi(blad)}
          akcja={
            <>
              <Button onClick={odswiez}>Spróbuj ponownie</Button>
              {filtrAktywny && <Button onClick={() => setParams(new URLSearchParams(), { replace: true })}>Wyczyść filtry</Button>}
            </>
          }
        />
      ) : dane === null ? (
        <StanLadowania tekst="Wczytuję historię wywołań..." />
      ) : dane.wiersze.length === 0 ? (
        <StanPusty
          tytul={filtrAktywny ? "Brak wywołań dla tych filtrów" : "Brak zapisanych wywołań"}
          opis={
            filtrAktywny
              ? "Zmień albo wyczyść filtry. Zapis obejmuje tylko wywołania od wdrożenia dziennika."
              : "Wywołania pojawią się, gdy ktoś uruchomi analizę, próbę mikrofonu albo sondę dostawców."
          }
          akcja={
            filtrAktywny ? (
              <Button onClick={() => setParams(new URLSearchParams(), { replace: true })}>Wyczyść filtry</Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <p className="developer-meta">
            Znaleziono {liczbaWpisow(dane.razem)}.
            {stronaPrzycieta && " Wybrana strona jest poza listą, więc pokazano ostatnią."}
          </p>
          <TabelaWywolan wiersze={dane.wiersze} kurs={dane.kurs} powrotSearch={search} />
          <Paginacja
            offset={dane.offset}
            limit={dane.limit}
            razem={dane.razem}
            naStronie={dane.wiersze.length}
            zajeta={ladowanie}
            onZmiana={(offset) => zmien({ offset: String(offset) })}
          />
        </>
      )}
    </Card>
  );
}
